/**
 * Ubicaciones por defecto para la aplicación
 */

export interface CityLandmarkLocation {
  city: string;
  country: string;
  displayName: string;
  landmarkName: string;
  latitude: number;
  longitude: number;
  aliases?: string[];
}

export interface TransportHub {
  city: string;
  type: 'airport' | 'bus_terminal' | 'train_station';
  displayName: string;
  name: string;
  latitude: number;
  longitude: number;
}

export const CITY_LANDMARK_LOCATIONS: CityLandmarkLocation[] = [
  {
    city: 'Buenos Aires',
    country: 'Argentina',
    displayName: 'Plaza de Mayo, Buenos Aires, Argentina',
    landmarkName: 'Plaza de Mayo',
    latitude: -34.608307,
    longitude: -58.371506,
    aliases: ['buenos aires capital', 'baires'],
  },
  {
    city: 'Salta',
    country: 'Argentina',
    displayName: 'Plaza 9 de Julio, Salta, Argentina',
    landmarkName: 'Plaza 9 de Julio',
    latitude: -24.789251,
    longitude: -65.410264,
    aliases: ['salta capital'],
  },
  {
    city: 'San Salvador de Jujuy',
    country: 'Argentina',
    displayName: 'Plaza Belgrano, San Salvador de Jujuy, Argentina',
    landmarkName: 'Plaza Belgrano',
    latitude: -24.185782,
    longitude: -65.299471,
    aliases: ['jujuy'],
  },
  {
    city: 'San Miguel de Tucumán',
    country: 'Argentina',
    displayName: 'Plaza Independencia, San Miguel de Tucumán, Argentina',
    landmarkName: 'Plaza Independencia',
    latitude: -26.83049,
    longitude: -65.20386,
    aliases: ['tucuman', 'tucumán'],
  },
];

export const TRANSPORT_HUBS: TransportHub[] = [
  // Buenos Aires
  {
    city: 'Buenos Aires',
    type: 'airport',
    displayName: 'Aeropuerto Internacional Ministro Pistarini, Buenos Aires, Argentina',
    name: 'Aeropuerto',
    latitude: -34.821943,
    longitude: -58.535239,
  },
  {
    city: 'Buenos Aires',
    type: 'bus_terminal',
    displayName: 'Estación Terminal de Ómnibus de Buenos Aires, Argentina',
    name: 'Terminal de Ómnibus',
    latitude: -34.603639,
    longitude: -58.381667,
  },
  {
    city: 'Buenos Aires',
    type: 'train_station',
    displayName: 'Estación Retiro, Buenos Aires, Argentina',
    name: 'Estación de Trenes',
    latitude: -34.593611,
    longitude: -58.371944,
  },
  // Salta
  {
    city: 'Salta',
    type: 'airport',
    displayName: 'Aeropuerto Internacional Martín Miguel de Güemes, Salta, Argentina',
    name: 'Aeropuerto',
    latitude: -24.859539,
    longitude: -65.488153,
  },
  {
    city: 'Salta',
    type: 'bus_terminal',
    displayName: 'Terminal de Ómnibus de Salta, Argentina',
    name: 'Terminal de Ómnibus',
    latitude: -24.795514,
    longitude: -65.398501,
  },
  {
    city: 'Salta',
    type: 'train_station',
    displayName: 'Estación de Trenes de Salta, Argentina',
    name: 'Estación de Trenes',
    latitude: -24.777077,
    longitude: -65.411283,
  },
  // Jujuy
  {
    city: 'San Salvador de Jujuy',
    type: 'airport',
    displayName: 'Aeropuerto Internacional Gobernador Horacio Guzmán, Jujuy, Argentina',
    name: 'Aeropuerto',
    latitude: -24.393452,
    longitude: -65.093695,
  },
  {
    city: 'San Salvador de Jujuy',
    type: 'bus_terminal',
    displayName: 'Nueva Terminal de Ómnibus, San Salvador de Jujuy, Argentina',
    name: 'Terminal de Ómnibus',
    latitude: -24.221433,
    longitude: -65.272321,
  },
  {
    city: 'San Salvador de Jujuy',
    type: 'train_station',
    displayName: 'Estación de Trenes de San Salvador de Jujuy, Argentina',
    name: 'Estación de Trenes',
    latitude: -24.183429,
    longitude: -65.298882,
  },
  // Tucumán
  {
    city: 'San Miguel de Tucumán',
    type: 'airport',
    displayName: 'Aeropuerto Internacional Teniente Benjamín Matienzo, Tucumán, Argentina',
    name: 'Aeropuerto',
    latitude: -26.83811,
    longitude: -65.106535,
  },
  {
    city: 'San Miguel de Tucumán',
    type: 'bus_terminal',
    displayName: 'Terminal de Ómnibus, San Miguel de Tucumán, Argentina',
    name: 'Terminal de Ómnibus',
    latitude: -26.835584,
    longitude: -65.193759,
  },
  {
    city: 'San Miguel de Tucumán',
    type: 'train_station',
    displayName: 'Estación de Trenes - Depósitos Fiscales, San Miguel de Tucumán, Argentina',
    name: 'Estación de Trenes',
    latitude: -26.819097,
    longitude: -65.211356,
  },
];

function normalize(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

export function findCityLandmarkByName(cityName: string): CityLandmarkLocation | null {
  const needle = normalize(cityName);
  if (!needle) return null;

  return (
    CITY_LANDMARK_LOCATIONS.find((location) => {
      const base = normalize(location.city);
      if (base === needle) return true;
      if (location.aliases?.some((alias) => normalize(alias) === needle)) return true;
      return false;
    }) || null
  );
}

export function findCityLandmarkByDisplayName(displayName: string): CityLandmarkLocation | null {
  const normalizedDisplay = normalize(displayName);
  if (!normalizedDisplay) return null;

  return (
    CITY_LANDMARK_LOCATIONS.find((location) => {
      const city = normalize(location.city);
      if (normalizedDisplay.includes(city)) return true;
      if (location.aliases?.some((alias) => normalizedDisplay.includes(normalize(alias)))) return true;
      return false;
    }) || null
  );
}

function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function findNearestCityLandmarkByCoords(
  latitude: number,
  longitude: number,
  maxDistanceKm = 120
): CityLandmarkLocation | null {
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;

  let best: CityLandmarkLocation | null = null;
  let bestDistance = Infinity;

  for (const location of CITY_LANDMARK_LOCATIONS) {
    const distance = haversineKm(latitude, longitude, location.latitude, location.longitude);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = location;
    }
  }

  if (!best || bestDistance > maxDistanceKm) return null;
  return best;
}

export function getAirportByCity(cityName: string): TransportHub | null {
  const needle = normalize(cityName);
  if (!needle) return null;

  return (
    TRANSPORT_HUBS.find((hub) => {
      const city = normalize(hub.city);
      return hub.type === 'airport' && city === needle;
    }) || null
  );
}

export function getBusTerminalByCity(cityName: string): TransportHub | null {
  const needle = normalize(cityName);
  if (!needle) return null;

  return (
    TRANSPORT_HUBS.find((hub) => {
      const city = normalize(hub.city);
      return hub.type === 'bus_terminal' && city === needle;
    }) || null
  );
}

export function getTrainStationByCity(cityName: string): TransportHub | null {
  const needle = normalize(cityName);
  if (!needle) return null;

  return (
    TRANSPORT_HUBS.find((hub) => {
      const city = normalize(hub.city);
      return hub.type === 'train_station' && city === needle;
    }) || null
  );
}

export const DEFAULT_LOCATIONS = {
  SALTA: {
    displayName: CITY_LANDMARK_LOCATIONS[0].displayName,
    latitude: CITY_LANDMARK_LOCATIONS[0].latitude,
    longitude: CITY_LANDMARK_LOCATIONS[0].longitude,
  },
} as const;

export type LocationData = typeof DEFAULT_LOCATIONS[keyof typeof DEFAULT_LOCATIONS];
